import { ReactNode, useEffect, useState } from 'react'
import { toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import { AccessDenied } from 'shared/errors/access-denied'
import { BadRequest } from 'shared/errors/bad-request'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useUserSettings } from '../../api/use-user-settings'
import { ProfileLayout } from '../../components/profile-layout'
import { useUpdateUserSettings } from '../api/use-update-user-settings'
import {
  EditableUserDomainAccountSettings,
  UserDomainAccountSettingsErrorsInterface,
  UserSettingsErrorResponseContentInterface,
} from '../types/user-settings-interface'
import UserDomainAccountFields from './user-domain-account-fields'
import UserSettingsSkeleton from './user-settings-skeleton'

const defaultValues: EditableUserDomainAccountSettings = {
  displayName: null,
  profileImage: null,
  bio: null,
}

const defaultErrors = {
  displayName: '',
  profileImage: '',
  bio: '',
}

function UserDomainAccountSettings(): ReactNode {
  const { t } = useTranslation()
  const { user, mutate: mutateUserData } = useUser()
  const { data: userSettings, error, mutate: mutateUserSettings } = useUserSettings()
  const { mutate: updateUserSettings, isPending } = useUpdateUserSettings({
    onSuccess: async response => {
      Promise.all([
        mutateUserSettings(response, false),
        mutateUserData(
          data =>
            data && {
              ...data,
              avatarUrl: response.userDomainAccount.profileImage?.path ?? '',
            },
          false,
        ),
      ])
      toastManager.add({ type: 'success', title: t('global.changes_saved') })
    },
    onError: error => {
      if (error instanceof BadRequest) {
        const userSettingErrors =
          error.errors as unknown as UserSettingsErrorResponseContentInterface

        setErrors({
          displayName: userSettingErrors.fields?.userDomainAccount?.displayName?.join(''),
        })
      }
    },
  })

  const [tempState, setTempState] = useState<EditableUserDomainAccountSettings>(defaultValues)
  const [errors, setErrors] = useState<UserDomainAccountSettingsErrorsInterface>(defaultErrors)

  useEffect(() => {
    if (error instanceof UnauthorizedResponse) {
      globalThis.location.replace(error.redirect)
      return
    } else if (error instanceof AccessDenied) {
      globalThis.location.replace('/403')
    }
  }, [error])

  useEffect(() => {
    if (userSettings?.user) {
      setTempState({
        ...userSettings.user,
        displayName: userSettings.userDomainAccount.displayName,
        profileImage: userSettings.userDomainAccount.profileImage,
        bio: userSettings.userDomainAccount.bio,
      })
    }
  }, [userSettings])

  return (
    <ProfileLayout
      title={t('profile.user_settings.profile.title')}
      isFetching={isPending}
      onSave={() =>
        updateUserSettings({
          userDomainAccount: {
            displayName: tempState.displayName,
            profileImage: tempState.profileImage?.id || null,
            bio: tempState.bio,
          },
        })
      }
    >
      {userSettings && isAuthorizedUser(user) ? (
        <div className="flex h-fit w-full flex-col gap-2 overflow-x-hidden rounded-md bg-bg-primary md:gap-5">
          <div className="bg-bg-accent/10 p-5">
            <span className="text-base font-bold text-text-primary">{`${t('global.email_address')}: ${
              userSettings.user.email
            }`}</span>
          </div>
          <div className="flex flex-col gap-7 px-5 pt-5 pb-7 md:gap-10 md:pb-10">
            <UserDomainAccountFields
              setProfileImage={profileImage => setTempState(prev => ({ ...prev, profileImage }))}
              setDisplayName={displayName => setTempState(prev => ({ ...prev, displayName }))}
              setDisplayBio={bio => setTempState(prev => ({ ...prev, bio }))}
              email={userSettings.user.email}
              errors={errors}
              displayName={tempState.displayName}
              bio={tempState.bio}
              profileImage={tempState.profileImage?.path || user.avatarUrl}
            />
          </div>
        </div>
      ) : (
        <UserSettingsSkeleton />
      )}
    </ProfileLayout>
  )
}

export default UserDomainAccountSettings
